/**
 * Notebook Monogram Customizer — Product Page Add-on
 * -----------------------------------------------------
 * Pairs with sections/monogram-customizer.liquid. Modeled directly on
 * assets/patch-customizer.js (same section-per-instance pattern, same
 * data-blob-then-render approach, same multi-line-item cart add with a
 * shared _customizer_id) — see that file and the "Custom Product Builder
 * Framework" doc for the shared conventions. The one real structural
 * difference: on the pouch, every patch comes from ONE shared catalog you
 * click directly. Here, each initial slot is TWO independent choices — a
 * typed LETTER and a picked COLOURWAY — and slot 2 (when the product
 * allows two initials) chooses its colourway completely independently of
 * slot 1's.
 *
 * Data flow:
 *  - The Liquid section renders one "notebook" object (the current
 *    product, including maxLetters derived from its letters:N tag) and a
 *    "colourways" array — every Letter Sticker colourway product in the
 *    configured collection, each carrying a `letters` map keyed A–Z to
 *    that colourway's variant for that letter ({variantId, available,
 *    image, price, priceFormatted}). Prices are integer cents (Liquid's
 *    `| json` on a Money value), same convention as the pouch — keep all
 *    math in cents, only format to a decimal string for display.
 *  - A slot is "complete" once it has both a letter (A–Z) and a colourway
 *    selected AND that colourway has an available variant for that
 *    letter. Slot 1 is required; slot 2 (if the product allows 2) is
 *    optional but must be either fully complete or fully empty — a
 *    half-filled second slot blocks Add to Cart with a specific error
 *    rather than silently being dropped.
 *  - On Add to Cart: one line item for the notebook variant (carrying a
 *    human-readable "Initials to Apply" build-sheet property, same idea
 *    as the pouch's "Patches to Apply"), plus one line item per completed
 *    slot for that slot's colourway+letter variant, carrying "Slot",
 *    "Letter", and "Colourway" properties. A shared _customizer_id ties
 *    every line item in the order back together. Because every sticker is
 *    a real product variant, Shopify handles pricing and per-letter
 *    inventory natively.
 */

(function () {
  'use strict';

  // Letter size as a % of the image box width, keyed by the notebook's MAX
  // initials (from its letters:N tag). Shared DEFAULT across every product
  // with that max count unless overridden per-product (see monogram-size /
  // monogram-top tags in the Liquid).
  var MONOGRAM_SIZE_BY_MAX = {
    1: 22,
    2: 16
  };

  // Each two-tone letter PNG is a square canvas with real blank space
  // around the glyph (measured directly off the live art: ~9-10% per side
  // on a wide letter like "A", ~19-20% per side on a narrower one like
  // "B") — that padding doesn't render, but it still counts as space
  // between the fixed BOX positions. A small positive gap here (the old
  // approach) left a visibly large gap between the actual glyphs once
  // that padding on both sides is added in. So this is a real overlap
  // (negative = boxes overlap) sized to cancel most of that padding for
  // a typical letter pair. Note for anyone tuning this further: with 2
  // fixed slots, the RIGHTMOST slot's position is independent of this
  // value (it's always ROW_RIGHT_MARGIN in from the edge) — only the
  // slot(s) to its left shift as this changes. Very narrow glyphs (e.g.
  // "I") will still show a bit more breathing room than wide ones; nudge
  // this if a specific letter combo looks off.
  var ROW_GAP_RATIO = -0.5;

  // Vertical center of the monogram row, as a % of the image box. Shared
  // DEFAULT unless a specific product overrides it (see monogram-top tag).
  var ROW_TOP = 76;

  // Margin between the row's right edge and the image's right edge, as a
  // % of the image box width. The row is right-anchored, not centered:
  // letters fill fixed slots from the right, so a single initial always
  // lands in the same rightmost slot a second initial would take (see
  // monogram-right tag).
  var ROW_RIGHT_MARGIN = 18;

  // Max rows shown in a colourway grid before it collapses behind a
  // "View More" button (same convention as the pouch's patch grid).
  var GRID_ROW_CAP = 3;

  // Word ordinals for slot headings ("Select your first initial and
  // style", "...second...", etc). Falls back to a numeric ordinal (4th,
  // 5th, ...) past the spelled-out list, so this still works if a future
  // product's letters:N tag ever asks for more than 3 initials.
  var ORDINAL_WORDS = ['first', 'second', 'third'];
  function ordinal(index) {
    if (ORDINAL_WORDS[index]) return ORDINAL_WORDS[index];
    var n = index + 1;
    var suffix = 'th';
    if (n % 10 === 1 && n % 100 !== 11) suffix = 'st';
    else if (n % 10 === 2 && n % 100 !== 12) suffix = 'nd';
    else if (n % 10 === 3 && n % 100 !== 13) suffix = 'rd';
    return n + suffix;
  }

  function slotSize(maxCount, override) {
    if (override) return override;
    return MONOGRAM_SIZE_BY_MAX[maxCount] || Math.max(10, 24 - maxCount * 4);
  }

  // Computes FIXED left/top/size slots for the notebook's full letter
  // capacity (maxCount), right-anchored and packed tight together — not
  // dependent on how many slots are currently complete. Slot positions
  // never move as letters are added or removed; only which fixed slots
  // are occupied changes (see renderMonogramRow). positions[0] is the
  // leftmost fixed slot, positions[maxCount - 1] is the rightmost.
  function computeFixedPositions(maxCount, sizeOverride, topOverride, rightOverride) {
    if (!maxCount) return [];
    var size = slotSize(maxCount, sizeOverride);
    var top = (topOverride || ROW_TOP) + '%';
    var gap = size * ROW_GAP_RATIO;
    var rowWidth = (maxCount * size) + ((maxCount - 1) * gap);
    var rightMargin = (rightOverride != null ? rightOverride : ROW_RIGHT_MARGIN);
    var rowRight = 100 - rightMargin;
    var startLeft = rowRight - rowWidth;
    var positions = [];

    for (var i = 0; i < maxCount; i++) {
      var left = startLeft + (i * (size + gap)) + (size / 2);
      positions.push({ top: top, left: left + '%', size: size + '%' });
    }

    return positions;
  }

  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var formatString = format || '\${{amount}}';
    return formatString.replace(/\{\{\s*amount\s*\}\}/, value);
  }

  function debounce(fn, wait) {
    var timeoutId;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  function MonogramCustomizer(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.moneyFormat = root.dataset.moneyFormat || '\${{amount}}';

    var dataEl = document.getElementById('mc-data-' + this.sectionId);
    this.data = JSON.parse(dataEl.textContent);
    this.notebook = this.data.notebook; // notebook.price is integer cents
    this.colourways = this.data.colourways || [];

    document.addEventListener('variant:change', this.handleVariantChange.bind(this));

    this.coverImageEl = document.getElementById('mc-cover-image-' + this.sectionId);
    this.rowEl = document.getElementById('mc-monogram-row-' + this.sectionId);
    this.slotsEl = document.getElementById('mc-slots-' + this.sectionId);
    this.totalPriceEl = document.getElementById('mc-total-price-' + this.sectionId);
    this.addToCartBtn = document.getElementById('mc-add-to-cart-' + this.sectionId);
    this.errorEl = document.getElementById('mc-error-' + this.sectionId);

    this.maxLetters = this.notebook.maxLetters;
    this.customizerId = 'mc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);

    // this.slots[i] = { letter: 'A'|'', colourwayId: string|null, expanded: bool }
    this.slots = [];
    for (var i = 0; i < this.maxLetters; i++) {
      this.slots.push({ letter: '', colourwayId: null, expanded: false });
    }

    this.addToCartBtn.addEventListener('click', this.handleAddToCart.bind(this));

    var accordionEl = this.root.querySelector('.monogram-customizer__accordion');
    if (accordionEl) {
      accordionEl.addEventListener('toggle', function () {
        if (accordionEl.open) this.applyAllGridCollapses();
      }.bind(this));
    }

    window.addEventListener('resize', debounce(function () {
      this.applyAllGridCollapses();
    }.bind(this), 150));

    if (!this.maxLetters || this.maxLetters < 1) {
      this.showError('No initial slots are configured for this notebook yet (check its letters:N tag).');
      return;
    }
    if (!this.colourways.length) {
      this.showError('No letter sticker colourways are configured yet (check this section’s "Letter sticker collection" setting).');
      return;
    }

    this.renderSlots();
    this.renderMonogramRow();
    this.updateTotal();
    this.updateAddToCartState();
  }

  MonogramCustomizer.prototype.handleVariantChange = function (event) {
    if (!event.detail || !event.detail.variant) return;
    var variant = event.detail.variant;
    this.notebook.variantId = variant.id;
    this.notebook.price = variant.price;
    this.notebook.available = variant.available;
    this.updateTotal();
    this.updateAddToCartState();
  };

  MonogramCustomizer.prototype.showError = function (message) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  };

  // A slot is "complete" once it has a letter and a colourway AND that
  // colourway's variant for that letter is available.
  MonogramCustomizer.prototype.getSlotVariant = function (slot) {
    if (!slot.letter || !slot.colourwayId) return null;
    var colourway = this.colourways.filter(function (c) { return c.id === slot.colourwayId; })[0];
    if (!colourway) return null;
    return colourway.letters[slot.letter] || null;
  };

  MonogramCustomizer.prototype.isSlotComplete = function (slot) {
    var variant = this.getSlotVariant(slot);
    return !!(variant && variant.available);
  };

  // A slot with something typed/picked but not yet complete (missing the
  // other field, or picked a combo that's out of stock).
  MonogramCustomizer.prototype.isSlotStarted = function (slot) {
    return !!(slot.letter || slot.colourwayId);
  };

  MonogramCustomizer.prototype.renderSlots = function () {
    var self = this;
    this.slotsEl.innerHTML = '';

    this.slots.forEach(function (slot, slotIndex) {
      var isOptional = slotIndex > 0;
      var wrap = document.createElement('div');
      wrap.className = 'monogram-customizer__slot';

      var heading = document.createElement('h4');
      heading.className = 'monogram-customizer__slot-heading';
      heading.textContent = 'Select your ' + ordinal(slotIndex) + ' initial and style' + (isOptional ? ' (optional)' : '');
      wrap.appendChild(heading);

      var letterRow = document.createElement('div');
      letterRow.className = 'monogram-customizer__slot-letter-row';

      var letterLabel = document.createElement('label');
      letterLabel.className = 'monogram-customizer__slot-letter-label';
      letterLabel.textContent = 'Letter';
      var letterInputId = 'mc-letter-' + self.sectionId + '-' + slotIndex;
      letterLabel.setAttribute('for', letterInputId);

      var letterInput = document.createElement('input');
      letterInput.type = 'text';
      letterInput.id = letterInputId;
      letterInput.className = 'monogram-customizer__slot-letter-input';
      letterInput.maxLength = 1;
      letterInput.autocomplete = 'off';
      letterInput.setAttribute('inputmode', 'text');
      letterInput.setAttribute('aria-label', 'Initial ' + (slotIndex + 1) + ' letter');
      letterInput.value = slot.letter;
      letterInput.addEventListener('input', function () {
        var clean = letterInput.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 1);
        letterInput.value = clean;
        slot.letter = clean;
        self.showError('');
        self.refreshColourwayGrid(slotIndex);
        self.renderMonogramRow();
        self.updateTotal();
        self.updateAddToCartState();
      });

      letterRow.appendChild(letterLabel);
      letterRow.appendChild(letterInput);
      wrap.appendChild(letterRow);

      var colourwayHeading = document.createElement('p');
      colourwayHeading.className = 'monogram-customizer__slot-colourway-label';
      colourwayHeading.textContent = 'Colourway';
      wrap.appendChild(colourwayHeading);

      var grid = document.createElement('div');
      grid.className = 'monogram-customizer__colourway-grid';
      grid.id = 'mc-colourway-grid-' + self.sectionId + '-' + slotIndex;
      wrap.appendChild(grid);

      var viewMoreBtn = document.createElement('button');
      viewMoreBtn.type = 'button';
      viewMoreBtn.className = 'monogram-customizer__colourway-viewmore';
      viewMoreBtn.id = 'mc-colourway-viewmore-' + self.sectionId + '-' + slotIndex;
      viewMoreBtn.hidden = true;
      viewMoreBtn.textContent = 'View More';
      viewMoreBtn.addEventListener('click', function () {
        slot.expanded = true;
        grid.classList.remove('is-collapsed');
        grid.style.maxHeight = '';
        viewMoreBtn.hidden = true;
      });
      wrap.appendChild(viewMoreBtn);

      self.slotsEl.appendChild(wrap);
    });

    this.slots.forEach(function (slot, slotIndex) {
      self.refreshColourwayGrid(slotIndex);
    });
  };

  // Rebuilds one slot's colourway swatch grid. Swatch preview image uses
  // that slot's currently typed letter when available, falling back to
  // "A" so a colourway is still recognizable before a letter is typed.
  MonogramCustomizer.prototype.refreshColourwayGrid = function (slotIndex) {
    var self = this;
    var slot = this.slots[slotIndex];
    var grid = document.getElementById('mc-colourway-grid-' + this.sectionId + '-' + slotIndex);
    if (!grid) return;

    grid.innerHTML = '';

    this.colourways.forEach(function (colourway) {
      var previewLetter = slot.letter || 'A';
      var previewVariant = colourway.letters[previewLetter] || colourway.letters['A'];
      var isSelected = slot.colourwayId === colourway.id;
      var isOutOfStockForLetter = !!(slot.letter && (!previewVariant || !previewVariant.available));

      var card = document.createElement('button');
      card.type = 'button';
      card.className = 'monogram-customizer__colourway-card';
      card.disabled = isOutOfStockForLetter;
      card.classList.toggle('is-selected', isSelected);

      var imgHtml = (previewVariant && previewVariant.image)
        ? '<img src="' + previewVariant.image + '" alt="" loading="lazy">'
        : '<span class="monogram-customizer__colourway-noimage" aria-hidden="true"></span>';

      card.innerHTML =
        imgHtml +
        '<span class="monogram-customizer__colourway-title">' + colourway.title + '</span>' +
        (isOutOfStockForLetter ? '<span class="monogram-customizer__sold-out">Sold out</span>' : '');

      card.addEventListener('click', function () {
        slot.colourwayId = colourway.id;
        self.showError('');
        self.refreshColourwayGrid(slotIndex);
        self.renderMonogramRow();
        self.updateTotal();
        self.updateAddToCartState();
      });

      grid.appendChild(card);
    });

    this.applyGridCollapse(slotIndex);
  };

  MonogramCustomizer.prototype.applyGridCollapse = function (slotIndex) {
    var slot = this.slots[slotIndex];
    var grid = document.getElementById('mc-colourway-grid-' + this.sectionId + '-' + slotIndex);
    var viewMoreBtn = document.getElementById('mc-colourway-viewmore-' + this.sectionId + '-' + slotIndex);
    if (!grid || !viewMoreBtn) return;

    grid.classList.remove('is-collapsed');
    grid.style.maxHeight = '';
    viewMoreBtn.hidden = true;

    if (slot.expanded) return;

    var cards = grid.querySelectorAll('.monogram-customizer__colourway-card');
    if (!cards.length) return;

    var gridRect = grid.getBoundingClientRect();
    if (gridRect.height === 0) return;

    var rowTops = [];
    cards.forEach(function (card) {
      var top = Math.round(card.getBoundingClientRect().top - gridRect.top);
      if (rowTops.indexOf(top) === -1) rowTops.push(top);
    });
    rowTops.sort(function (a, b) { return a - b; });

    if (rowTops.length <= GRID_ROW_CAP) return;

    var cutoffTop = rowTops[GRID_ROW_CAP - 1];
    var maxBottom = 0;
    cards.forEach(function (card) {
      var rect = card.getBoundingClientRect();
      var top = Math.round(rect.top - gridRect.top);
      if (top === cutoffTop) {
        var bottom = top + rect.height;
        if (bottom > maxBottom) maxBottom = bottom;
      }
    });

    grid.style.maxHeight = maxBottom + 'px';
    grid.classList.add('is-collapsed');
    viewMoreBtn.hidden = false;
  };

  MonogramCustomizer.prototype.applyAllGridCollapses = function () {
    var self = this;
    this.slots.forEach(function (slot, slotIndex) {
      if (!slot.expanded) self.applyGridCollapse(slotIndex);
    });
  };

  // Renders the actual letters on the cover photo — only COMPLETE slots,
  // in slot order, into FIXED right-anchored positions. The rightmost
  // fixed slot always holds the most recently completed slot: with one
  // initial complete, it sits in that rightmost slot; completing a second
  // initial doesn't move the first slot's position definition, it just
  // shifts which fixed slot each complete slot maps to, so slot 1 lands
  // in the next slot to the left and slot 2 takes the rightmost one.
  MonogramCustomizer.prototype.renderMonogramRow = function () {
    var self = this;
    var completeSlots = this.slots.filter(function (slot) { return self.isSlotComplete(slot); });

    var fixedPositions = computeFixedPositions(
      this.maxLetters,
      this.notebook.sizeOverride,
      this.notebook.topOverride,
      this.notebook.rightOverride
    );
    var offset = fixedPositions.length - completeSlots.length;

    this.rowEl.innerHTML = '';

    completeSlots.forEach(function (slot, index) {
      var variant = self.getSlotVariant(slot);
      var pos = fixedPositions[offset + index];
      var item = document.createElement('div');
      item.className = 'monogram-customizer__monogram-row-item';
      item.style.top = pos.top;
      item.style.left = pos.left;
      item.style.width = pos.size;
      item.style.height = pos.size;
      if (variant && variant.image) {
        item.innerHTML = '<img src="' + variant.image + '" alt="' + slot.letter + '">';
      }
      self.rowEl.appendChild(item);
    });
  };

  MonogramCustomizer.prototype.updateTotal = function () {
    var self = this;
    var totalCents = this.notebook.price;
    this.slots.forEach(function (slot) {
      var variant = self.getSlotVariant(slot);
      if (variant) totalCents += variant.price;
    });
    this.totalPriceEl.innerHTML = formatMoney(totalCents, this.moneyFormat);
  };

  // Slot 1 must be complete. Slot 2 (if present) must be either fully
  // complete or fully empty — a half-filled slot 2 blocks checkout with a
  // specific error rather than silently being dropped from the order.
  MonogramCustomizer.prototype.validate = function () {
    var requiredSlot = this.slots[0];
    if (!this.isSlotComplete(requiredSlot)) {
      if (this.isSlotStarted(requiredSlot)) {
        return 'Pick both a letter and a colourway for Initial 1.';
      }
      return 'Pick a letter and a colourway for Initial 1.';
    }

    for (var i = 1; i < this.slots.length; i++) {
      var slot = this.slots[i];
      if (this.isSlotStarted(slot) && !this.isSlotComplete(slot)) {
        if (!slot.letter || !slot.colourwayId) {
          return 'Finish Initial ' + (i + 1) + ' (pick both a letter and a colourway), or clear it to leave it off.';
        }
        return 'That letter/colourway combination for Initial ' + (i + 1) + ' is out of stock — pick a different colourway or letter.';
      }
    }

    return null;
  };

  MonogramCustomizer.prototype.updateAddToCartState = function () {
    var error = this.validate();
    this.addToCartBtn.disabled = !!error || !this.notebook.available;
  };

  MonogramCustomizer.prototype.handleAddToCart = function () {
    var self = this;
    var error = this.validate();
    if (error) {
      this.showError(error);
      return;
    }

    this.addToCartBtn.disabled = true;
    this.addToCartBtn.textContent = 'Adding...';
    this.showError('');

    var completeSlots = this.slots
      .map(function (slot, index) { return { slot: slot, index: index }; })
      .filter(function (entry) { return self.isSlotComplete(entry.slot); });

    var summary = completeSlots
      .map(function (entry) {
        var colourway = self.colourways.filter(function (c) { return c.id === entry.slot.colourwayId; })[0];
        return (entry.index + 1) + ') ' + entry.slot.letter + ' — ' + (colourway ? colourway.title : '');
      })
      .join(',  ');

    var items = [];

    items.push({
      id: this.notebook.variantId,
      quantity: 1,
      properties: {
        '_customizer_id': this.customizerId,
        'Notebook Style': this.notebook.title,
        'Initials to Apply': summary
      }
    });

    completeSlots.forEach(function (entry) {
      var variant = self.getSlotVariant(entry.slot);
      var colourway = self.colourways.filter(function (c) { return c.id === entry.slot.colourwayId; })[0];
      items.push({
        id: variant.variantId,
        quantity: 1,
        properties: {
          '_customizer_id': self.customizerId,
          'Slot': 'Initial ' + (entry.index + 1),
          'Letter': entry.slot.letter,
          'Colourway': colourway ? colourway.title : '',
          'Notebook': self.notebook.title
        }
      });
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw new Error(err.description || 'Something went out of stock. Please pick another letter or colourway.');
          });
        }
        return response.json();
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('monogram-customizer:added', {
          detail: { customizerId: self.customizerId }
        }));
        window.location.href = '/cart';
      })
      .catch(function (err) {
        self.showError(err.message);
        self.addToCartBtn.disabled = false;
        self.addToCartBtn.textContent = 'Add to Cart';
      });
  };

  function init() {
    document.querySelectorAll('.monogram-customizer').forEach(function (el) {
      new MonogramCustomizer(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
